import { IObservableArray, makeAutoObservable, observable, isObservable } from 'mobx';
import { orderBy } from 'lodash';

import { GridDataEntry } from 'controls/grid/gridDataEntry';
import { DataProvider } from 'controls/grid/gridConfiguration';
import { isValid, RulesHandler } from 'controls/queryBuilder/ruleDefinition';
import { GridStore } from 'controls/grid/gridStore';
import { MobxManager } from 'framework/mobx-integration';
import { GridDataItem } from 'controls/grid/gridDataItem';

export class ArrayDataProvider<DataEntry extends GridDataEntry> implements DataProvider<DataEntry> {
	allData: IObservableArray<DataEntry>;

	onlySelected: boolean = false;

	initialized: boolean;
	store: GridStore<DataEntry>;
	showLoader: boolean;

	mobxManager = new MobxManager();

	constructor(data: Array<DataEntry>) {
		this.updateData(data);

		makeAutoObservable(this);
	}

	failToLoad: boolean;
	groupBySupported?: boolean;

	convertToDataItems(data: Array<DataEntry>) {
		return data.map((x, i) => this.convertToDataItem(x, i));
	}

	convertToDataItem(x: DataEntry, index?: number) {
		return {
			id: x[this.store.idKey] as string,
			type: 'data' as const,
			groupLevel: 1,
			data: x,
			index,
		};
	}

	updateData(data: Array<DataEntry>) {
		this.updateDataRaw(data);
	}

	updateDataRaw(data: Array<DataEntry>) {
		if (isObservable(data)) {
			this.allData = data as IObservableArray<DataEntry>;
		} else {
			this.allData = observable.array(data);
		}
	}

	get scrollableRowsCount() {
		return this.data.length;
	}

	get totalRowsCount() {
		return this.allData.length;
	}

	get visibleRowsCount() {
		return this.data.length;
	}

	silentReload(): void {}

	get data() {
		return observable.array(this.convertToDataItems(this.getRefreshedData()));
	}

	attach(store: GridStore<DataEntry>) {
		this.store = store;
		this.initialized = true;
		return Promise.resolve();
	}

	get(index: number): GridDataItem<DataEntry> {
		if (this.data.length <= index) return null;

		return this.data[index];
	}

	informVisibleRangeChanged(): void {}

	getRefreshedData = () => {
		if (!this.store) return [];

		if (!isValid(this.store.actualFilter)) {
			return this.sortData(this.allData);
		}

		const handler = new RulesHandler(this.store.filtersConfigurationEffective);
		let result = handler.filter(this.allData, this.store.actualFilter);

		if (this.onlySelected) {
			result = result.filter((x) => this.store.selection.isSelected(this.convertToDataItem(x)));
		}

		if (!this.store.actualSorting) {
			return result;
		}

		return this.sortData(result);
	};

	defaultSortValue = (field: keyof DataEntry) => {
		return (item: DataEntry) => {
			const value = item[field];
			if (typeof value == 'string') {
				return value.toLowerCase();
			}
			return value;
		};
	};

	sortData(data: IObservableArray<DataEntry> | Array<DataEntry>) {
		const columns = this.store.columns.config;
		return orderBy(
			data,
			this.store.actualSorting.map((x) => {
				const column = columns.find((y) => y.field == x.field);
				return column.sortValue ?? this.defaultSortValue(x.field as keyof DataEntry);
			}),
			this.store.actualSorting.map((x) => x.dir),
		);
	}

	destroy(): void {
		this.mobxManager.destroy();
	}
}
